import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ReferenceLine,
} from "recharts";

interface SceneData {
  id: string | number;
  name: string;
  score: number;
  score_max: number;
}

interface SuccessRatePerSceneChartProps {
  data: SceneData[];
  showAverageLine?: boolean;
  isLoading?: boolean;
  selectedSceneId?: string | number | null;
  onSceneClick?: (sceneId: string | number, sceneName: string) => void;
}

const MAX_LABEL_LENGTH = 20;

const getBarColor = (percentage: number): string => {
  if (percentage >= 80) return "#00c889";
  if (percentage >= 50) return "#eab308";
  return "#ef4444";
};

// Truncate label for Y-axis display
const truncateLabel = (label: string) => {
  if (!label) return "";
  return label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH)}...` : label;
};

const SuccessRatePerSceneChart = ({
  data,
  showAverageLine = true,
  isLoading = false,
  selectedSceneId,
  onSceneClick,
}: SuccessRatePerSceneChartProps) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    return data.map((scene) => ({
      ...scene,
      percentage: scene.score_max > 0 ? (scene.score / scene.score_max) * 100 : 0,
    }));
  }, [data]);

  const averagePercentage = useMemo(() => {
    if (chartData.length === 0) return 0;
    const total = chartData.reduce((sum, scene) => sum + scene.percentage, 0);
    return total / chartData.length;
  }, [chartData]);

  const hasData = chartData.length > 0;

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
          <span className="text-sm text-gray-500">{t("statsNew.charts.loading")}</span>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        {t("statsNew.charts.noData")}
      </div>
    );
  }

  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 50)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={true}
            vertical={false}
            stroke="#e5e7eb"
          />
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fontSize: 12, fill: "#6b7280" }}
            tickLine={false}
            axisLine={{ stroke: "#e5e7eb" }}
            tickFormatter={(value) => `${value}%`}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 12, fill: "#374151" }}
            tickLine={false}
            axisLine={{ stroke: "#e5e7eb" }}
            width={150}
            tickFormatter={truncateLabel}
            interval={0}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
            }}
            cursor={{ fill: "rgba(0, 200, 137, 0.1)" }}
            formatter={(value: number) => [
              `${value.toFixed(1)}%`,
              t("statsNew.charts.successRate"),
            ]}
            labelFormatter={(label: string, payload: Array<{ payload?: { name?: string } }>) => {
              if (payload && payload[0]?.payload?.name) {
                return payload[0].payload.name;
              }
              return label;
            }}
          />
          {showAverageLine && (
            <ReferenceLine
              x={averagePercentage}
              stroke="#6b7280"
              strokeDasharray="5 5"
              label={{
                value: `${t("statsNew.charts.average")}: ${averagePercentage.toFixed(1)}%`,
                position: "top",
                fill: "#6b7280",
                fontSize: 11,
              }}
            />
          )}
          <Bar
            dataKey="percentage"
            radius={[0, 4, 4, 0]}
            maxBarSize={30}
            cursor={onSceneClick ? "pointer" : "default"}
            onClick={(data) => {
              if (onSceneClick && data?.id) {
                onSceneClick(data.id, data.name);
              }
            }}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getBarColor(entry.percentage)}
                opacity={selectedSceneId && selectedSceneId !== entry.id ? 0.3 : 1}
                stroke={selectedSceneId === entry.id ? "#1f2937" : "none"}
                strokeWidth={selectedSceneId === entry.id ? 2 : 0}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SuccessRatePerSceneChart;
